import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RequestsTable } from '@/components/app/requests-table';
import { CreateRequestDialog } from '@/routes/requests/new';
import { RequestsQueuePage } from '@/routes/requests/queue';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { hasCapability } from '@/lib/rbac';
import { licenseRequestsListResponseSchema } from '@/schemas/license-requests';

async function fetchRequests(endpoint: string) {
  const resp = await apiFetch(endpoint);
  const json = await resp.json();
  return licenseRequestsListResponseSchema.parse(json);
}

function TableSkeleton() {
  return (
    <div className="mt-6 space-y-2">
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-9 flex-1" />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, ri) => (
        <div key={ri} className="flex gap-2">
          {Array.from({ length: 6 }).map((_, ci) => (
            <Skeleton key={`${ri}-${ci}`} className="h-11 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** «Мои заявки» — manufacturer sale notifications list (#73). */
function ManufacturerRequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const limit = Math.max(1, parseInt(searchParams.get('limit') ?? '20', 10));

  const endpoint = useMemo(() => `/license-requests?page=${page}&limit=${limit}`, [page, limit]);

  const query = useQuery({
    queryKey: ['license-requests', 'list', endpoint],
    queryFn: () => fetchRequests(endpoint),
    staleTime: 30_000,
  });

  const setPage = (next: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('page', String(next));
      return p;
    });
  };

  const setLimit = (next: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('limit', String(next));
      p.set('page', '1');
      return p;
    });
  };

  const openCreate = (open: boolean) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (open) p.set('action', 'new');
      else p.delete('action');
      return p;
    });
  };

  const data = query.data;
  const isFirstLoad = query.isLoading && !data;
  const isRefetching = query.isFetching && !query.isLoading;
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      <CreateRequestDialog open={searchParams.get('action') === 'new'} onOpenChange={openCreate} />

      {/* Page head */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('requests.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('requests.subtitle')}
          </p>
        </div>
        <Button className="mt-2.5 shrink-0" onClick={() => openCreate(true)}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('requests.create')}
        </Button>
      </div>

      {/* Content */}
      {query.isError ? (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{t('requests.listError')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : isFirstLoad ? (
        <TableSkeleton />
      ) : total === 0 ? (
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
            {t('requests.emptyTitle')}
          </h2>
          <p className="max-w-[42ch] text-center text-[14.5px] text-muted-foreground">
            {t('requests.emptyHint')}
          </p>
          <Button className="mt-1" onClick={() => openCreate(true)}>
            {t('requests.emptyCta')}
          </Button>
        </div>
      ) : (
        <div
          className={`transition-opacity ${isRefetching ? 'pointer-events-none opacity-60' : ''}`}
        >
          <RequestsTable data={data?.data ?? []} />
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <span className="text-[13px] tabular-nums text-faint">
            {t('common.showingRange', { start, end, total })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[64px] text-center text-[13px] tabular-nums text-muted-foreground">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page + 1)}
              disabled={page >= pages}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
              <SelectTrigger
                className="h-8 w-[72px] border-input bg-transparent text-[13px]"
                aria-label={t('common.pageSize')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'Requests' };

/**
 * /requests is role-aware: reviewers (developer_operator/admin) get the
 * review queue (#74); manufacturers get their own sale-notification list (#73).
 */
export default function RequestsPage() {
  const { user } = useAuth();
  if (user && hasCapability(user.role, 'reviewLicenseRequests')) {
    return <RequestsQueuePage />;
  }
  return <ManufacturerRequestsPage />;
}
